import type { SpecialZoneReservationOrderVoData } from "@/api/services/specialZoneReservationOrderVoTypes";
import { DEFAULT_VALUE } from "@/config";
import { dateTimeFormat } from "@/utils";
import { displayZoneContractCustomerText } from "@/views/pages/tradeManage/zoneContract/zoneContractCustomerLabel";
import { ZONE_CONTRACT_GOODS, ZONE_CONTRACT_RESERVATION_ORDER_STATE, ZONE_CONTRACT_SPECIAL_ZONE } from "@zhihaoscm/meta";
import type {
    SpecialZoneContractGoods,
    SpecialZoneHandover,
    SpecialZoneHandoverDetail,
    SpecialZoneReservationOrderGoods,
    SpecialZoneReservationOrderPagingRow,
    SpecialZoneWeighDetailVo,
    SpecialZoneWeighVo
} from "@zhihaoscm/entity";

const RES_ORDER_STATE = ZONE_CONTRACT_RESERVATION_ORDER_STATE;

/** 与列表页、汇总列一致的物料规格顺序 */
export const ZONE_RESERVATION_MATERIAL_SPECS: { id: string; name: string }[] = [...ZONE_CONTRACT_GOODS.list]
    .sort((a, b) => Number(a.code) - Number(b.code))
    .map(meta => ({
        id: String(meta.code),
        name: meta.intro
    }));

/** 列表汇总、列 formatter 使用的按物料编码聚合指标（字段名与 `SpecialZoneReservationOrderGoods` 一致） */
export interface ReservationOrderGoodsMetricsBySpec {
    plannedTonnage: number;
    settlementTon: number;
    settlementAmount: number;
    unitPrice: number;
}

export function pickReservationTransportDisplay(
    transportOrderShipId: string | null | undefined,
    requirementPlatId: string | null | undefined
): string {
    const ship = String(transportOrderShipId ?? "").trim();
    if (ship) return ship;
    const plat = String(requirementPlatId ?? "").trim();
    if (plat) return plat;
    return "";
}

/** 接口日期：ISO 字符串或含 `T` 的字符串，展示为 `YYYY-MM-DD HH:mm` */
export function displayApiDateTime(raw: string | undefined | null): string {
    if (!raw) return DEFAULT_VALUE;
    const normalized = raw.trim();
    if (!normalized) return DEFAULT_VALUE;
    return dateTimeFormat(normalized);
}

/** VO / OpenAPI 中标为 Record 的日期字段，规范为 ISO 或空串 */
export function coerceVoDateToString(v: unknown): string {
    if (v == null || v === "") return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" && Number.isFinite(v)) return new Date(v).toISOString();
    return "";
}

export function displayApiDateTimeUnknown(raw: unknown): string {
    return displayApiDateTime(coerceVoDateToString(raw));
}

export function reservationOrderStateLabel(state: number | undefined): string {
    if (state == null || Number.isNaN(Number(state))) return DEFAULT_VALUE;
    const code = Number(state);
    const meta = RES_ORDER_STATE.list.find(m => Number(m.code) === code);
    return meta?.intro ?? DEFAULT_VALUE;
}

function gLastUnitPriceForGoodsCode(
    goods: SpecialZoneReservationOrderGoods[] | undefined,
    goodsCodeKey: string
): number {
    let u = 0;
    for (const g of goods ?? []) {
        if (String(g.goodsCode) === goodsCodeKey) u = g.unitPrice ?? 0;
    }
    return u;
}

/** 按 `goodsCode` 聚合当前分页行物料（用于列表子列与合计） */
export function aggregateReservationGoodsMetricsBySpec(
    goods: SpecialZoneReservationOrderGoods[] | undefined,
    specs: { id: string }[] = ZONE_RESERVATION_MATERIAL_SPECS
): Record<string, ReservationOrderGoodsMetricsBySpec> {
    const metrics: Record<string, ReservationOrderGoodsMetricsBySpec> = {};
    for (const s of specs) {
        metrics[s.id] = { plannedTonnage: 0, settlementTon: 0, settlementAmount: 0, unitPrice: 0 };
    }
    for (const g of goods ?? []) {
        const key = String(g.goodsCode);
        if (!metrics[key]) {
            metrics[key] = { plannedTonnage: 0, settlementTon: 0, settlementAmount: 0, unitPrice: 0 };
        }
        const m = metrics[key]!;
        m.plannedTonnage += g.plannedTonnage ?? 0;
        m.settlementTon += g.settlementTon ?? 0;
        m.settlementAmount += g.settlementAmount ?? 0;
    }
    for (const k of Object.keys(metrics)) {
        const m = metrics[k]!;
        if (m.settlementTon > 0) {
            m.unitPrice = Math.round((m.settlementAmount / m.settlementTon) * 100) / 100;
        } else if (m.plannedTonnage > 0) {
            m.unitPrice = Math.round(gLastUnitPriceForGoodsCode(goods, k) * 100) / 100;
        }
    }
    return metrics;
}

type ReservationPagingRowWithSnakeCustomer = SpecialZoneReservationOrderPagingRow & {
    business_customer?: SpecialZoneReservationOrderPagingRow["businessCustomer"];
};

export function businessCustomerFromReservationPagingRow(
    row: SpecialZoneReservationOrderPagingRow
): SpecialZoneReservationOrderPagingRow["businessCustomer"] {
    const ext = row as ReservationPagingRowWithSnakeCustomer;
    return ext.businessCustomer ?? ext.business_customer;
}

export function zoneNameFromContractCore(
    contract: SpecialZoneReservationOrderPagingRow["specialZoneContract"] | undefined
): string {
    const zid = contract?.specialZoneId;
    if (zid == null) return DEFAULT_VALUE;
    const meta = ZONE_CONTRACT_SPECIAL_ZONE.list.find(m => Number(m.code) === Number(zid));
    return meta?.intro ?? DEFAULT_VALUE;
}

export function buyerUserTextFromReservationPagingRow(row: SpecialZoneReservationOrderPagingRow): string {
    const order = row.specialZoneReservationOrder;
    const contract = row.specialZoneContract;
    const bc = businessCustomerFromReservationPagingRow(row);
    const idFromBc = bc?.id != null && Number(bc.id) > 0 ? Number(bc.id) : 0;
    const idFromContract =
        contract?.customerId != null && Number(contract.customerId) > 0 ? Number(contract.customerId) : 0;
    const idFromOrder = order?.customerId != null && Number(order.customerId) > 0 ? Number(order.customerId) : 0;
    const customerIdNum = idFromBc || idFromContract || idFromOrder;
    return displayZoneContractCustomerText(
        customerIdNum > 0
            ? {
                  id: customerIdNum,
                  code: bc?.code,
                  applyState: bc?.applyState,
                  institutionName: bc?.institutionName,
                  realName: bc?.realName,
                  nickName: bc?.nickName,
                  mobile: bc?.mobile ?? order?.mobile
              }
            : null
    );
}

export function shipDisplayNameFromReservationPagingRow(row: SpecialZoneReservationOrderPagingRow): string {
    const order = row.specialZoneReservationOrder;
    const ship = row.ship;
    const name = order?.shipName || ship?.cnname || ship?.name;
    return (name && String(name).trim()) || DEFAULT_VALUE;
}

export function sumContractGoodsTotalTon(contractGoods: SpecialZoneContractGoods[] | undefined): number {
    if (!contractGoods?.length) return 0;
    return contractGoods
        .filter(g => g.goodsCode !== 0 && g.goodsName !== "汇总")
        .reduce((s, g) => s + (g.totalTon ?? 0), 0);
}

function reservationVoAsRecord(vo: SpecialZoneReservationOrderVoData): Record<string, unknown> {
    return vo as unknown as Record<string, unknown>;
}

function asRecord(v: unknown): Record<string, unknown> | undefined {
    return v != null && typeof v === "object" ? (v as Record<string, unknown>) : undefined;
}

function firstArray<T>(...candidates: unknown[]): T[] {
    for (const c of candidates) {
        if (Array.isArray(c)) return c as T[];
    }
    return [];
}

function pickWeighPayload(vo: SpecialZoneReservationOrderVoData): {
    weigh?: SpecialZoneWeighVo;
    details: SpecialZoneWeighDetailVo[];
} {
    const r = reservationVoAsRecord(vo);
    const weighmentRow = asRecord(r.special_zone_weighment ?? r.specialZoneWeighment);
    const weighDirect = asRecord(vo.specialZoneWeigh ?? r.special_zone_weigh);
    const weighContainer = asRecord(weighDirect?.specialZoneWeigh ?? weighDirect?.special_zone_weigh);
    const weigh = (weighContainer ?? weighDirect ?? asRecord(weighmentRow?.specialZoneWeigh ?? weighmentRow?.special_zone_weigh)) as
        | SpecialZoneWeighVo
        | undefined;
    const details = firstArray<SpecialZoneWeighDetailVo>(
        vo.specialZoneWeighDetails,
        r.special_zone_weigh_details,
        weighDirect?.specialZoneWeighDetails,
        weighDirect?.special_zone_weigh_details,
        weighContainer?.specialZoneWeighDetails,
        weighContainer?.special_zone_weigh_details,
        weighmentRow?.specialZoneWeighDetails,
        weighmentRow?.special_zone_weigh_details
    );
    return { weigh, details };
}

function pickHandoverPayload(vo: SpecialZoneReservationOrderVoData): {
    hand?: SpecialZoneHandover;
    details: SpecialZoneHandoverDetail[];
} {
    const r = reservationVoAsRecord(vo);
    const handDirect = asRecord(vo.specialZoneHandover ?? r.special_zone_handover);
    const handContainer = asRecord(handDirect?.specialZoneHandover ?? handDirect?.special_zone_handover);
    const hand = (handContainer ?? handDirect) as SpecialZoneHandover | undefined;
    const details = firstArray<SpecialZoneHandoverDetail>(
        vo.specialZoneHandoverDetails,
        r.special_zone_handover_details,
        handDirect?.specialZoneHandoverDetails,
        handDirect?.special_zone_handover_details,
        handContainer?.specialZoneHandoverDetails,
        handContainer?.special_zone_handover_details
    );
    return { hand, details };
}

/** 预约单 VO 内嵌合同物料：camelCase 或 snake_case */
export function getEmbeddedContractGoodsFromReservationVo(vo: SpecialZoneReservationOrderVoData): SpecialZoneContractGoods[] {
    const camel = vo.specialZoneContractGoods;
    if (camel != null && camel.length > 0) return camel;
    const snake = reservationVoAsRecord(vo).special_zone_contract_goods;
    if (Array.isArray(snake) && snake.length > 0) return snake as SpecialZoneContractGoods[];
    return [];
}

export function goodsIntroByGoodsCode(goodsCode: number | null | undefined): string {
    if (goodsCode == null || Number.isNaN(Number(goodsCode))) return DEFAULT_VALUE;
    return ZONE_CONTRACT_GOODS.getIntroByCode(goodsCode) ?? DEFAULT_VALUE;
}

function pad2(n: number): string {
    return n < 10 ? `0${n}` : String(n);
}

function formatLocalDateTimeMinute(dt: Date): string | undefined {
    if (Number.isNaN(dt.getTime())) return undefined;
    return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())} ${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
}

function formatLocalDateTimeSecond(dt: Date): string | undefined {
    if (Number.isNaN(dt.getTime())) return undefined;
    return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())} ${pad2(dt.getHours())}:${pad2(dt.getMinutes())}:${pad2(dt.getSeconds())}`;
}

function parseVoDateToDate(raw: unknown): Date | undefined {
    if (raw == null || raw === "") return undefined;
    if (typeof raw === "string") {
        const s = raw.trim();
        if (!s) return undefined;
        const iso = coerceVoDateToString(s);
        const fromIso = iso ? new Date(iso) : new Date(s);
        return Number.isNaN(fromIso.getTime()) ? undefined : fromIso;
    }
    if (typeof raw === "number" && Number.isFinite(raw)) {
        const dt = new Date(raw);
        return Number.isNaN(dt.getTime()) ? undefined : dt;
    }
    if (Array.isArray(raw)) {
        const nums = raw.slice(0, 6).map(x => Number(x));
        if (nums.length >= 3 && nums.every(n => Number.isFinite(n))) {
            const [y, mo, d, h = 0, mi = 0, s = 0] = nums;
            const monthIdx = mo >= 1 && mo <= 12 ? mo - 1 : mo;
            const dt = new Date(y, monthIdx, d, h, mi, s);
            return Number.isNaN(dt.getTime()) ? undefined : dt;
        }
        return undefined;
    }
    if (typeof raw === "object") {
        const o = raw as Record<string, unknown>;
        const y = Number(o.year ?? o.y);
        const moNum = Number(o.monthValue ?? o.month ?? o.M);
        const d = Number(o.dayOfMonth ?? o.day ?? o.d);
        if (Number.isFinite(y) && Number.isFinite(moNum) && Number.isFinite(d) && y > 1970) {
            const monthIdx =
                o.monthValue != null && Number.isFinite(Number(o.monthValue))
                    ? Number(o.monthValue) - 1
                    : moNum >= 1 && moNum <= 12
                      ? moNum - 1
                      : moNum;
            const h = Number(o.hour ?? o.hourOfDay ?? 0);
            const mi = Number(o.minute ?? 0);
            const s = Number(o.second ?? 0);
            const dt = new Date(y, monthIdx, d, h, mi, s);
            return Number.isNaN(dt.getTime()) ? undefined : dt;
        }
    }
    const coerced = coerceVoDateToString(raw);
    if (!coerced) return undefined;
    const dt = new Date(coerced);
    return Number.isNaN(dt.getTime()) ? undefined : dt;
}

/** 解析过磅主表 `loadingEndTime`（Jackson 多种形态） */
export function formatWeighLoadingEndTimeForDisplay(raw: unknown): string | undefined {
    const dt = parseVoDateToDate(raw);
    return dt ? formatLocalDateTimeMinute(dt) : undefined;
}

export function pickSpecialZoneWeighDetailsFromReservationVo(
    vo: SpecialZoneReservationOrderVoData
): SpecialZoneWeighDetailVo[] {
    return pickWeighPayload(vo).details;
}

/** 过磅附件文件 ID：缺省时返回空数组，便于详情页统一渲染。 */
export function pickSpecialZoneWeighAttachmentFileIdsFromReservationVo(
    vo: SpecialZoneReservationOrderVoData
): number[] {
    const { weigh } = pickWeighPayload(vo);
    if (!Array.isArray(weigh?.attachmentFileIds)) return [];
    return weigh.attachmentFileIds
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0);
}

/** 装船过磅单 Tab 表头：编号、装船结束时间（展示用字符串） */
export function weighBillHeaderFieldsFromReservationVo(vo: SpecialZoneReservationOrderVoData): {
    specialZoneWeighId: string;
    loadingEndTime: string;
} {
    const { weigh, details } = pickWeighPayload(vo);
    let specialZoneWeighId = "";
    let loadingEndTime = "";
    if (weigh) {
        const rec = weigh as unknown as Record<string, unknown>;
        const idFromWeigh = String(weigh.id ?? "").trim();
        const idFromDetail = details.map(d => d?.weighId).find(wid => wid != null && String(wid).trim() !== "");
        specialZoneWeighId =
            idFromWeigh ||
            (idFromDetail != null ? String(idFromDetail).trim() : "") ||
            String(rec.weighNo ?? rec.weigh_no ?? "").trim();
        loadingEndTime = formatWeighLoadingEndTimeForDisplay(weigh.loadingEndTime ?? rec.loading_end_time) ?? "";
    } else if (details.length > 0) {
        const idFromDetail = details.map(d => d?.weighId).find(wid => wid != null && String(wid).trim() !== "");
        if (idFromDetail) specialZoneWeighId = String(idFromDetail).trim();
    }
    return { specialZoneWeighId, loadingEndTime };
}

/** 编辑过磅单提交时需要的过磅单主表字段。 */
export function weighBillApiFieldsFromReservationVo(vo: SpecialZoneReservationOrderVoData): {
    specialZoneWeighId: string;
    loadingEndTime: string;
} {
    const { weigh, details } = pickWeighPayload(vo);
    const detailWeighId = details
        .map(detail => detail?.weighId)
        .find(value => value != null && String(value).trim() !== "");
    const specialZoneWeighId =
        String(weigh?.id ?? "").trim() ||
        (detailWeighId != null ? String(detailWeighId).trim() : "") ||
        "";
    const loadingEndTime = weigh ? formatWeighLoadingEndTimeForApi(weigh.loadingEndTime) ?? "" : "";
    return { specialZoneWeighId, loadingEndTime };
}

/** 过磅主表时间转接口入参格式 `YYYY-MM-DD HH:mm:ss`。 */
export function formatWeighLoadingEndTimeForApi(raw: unknown): string | undefined {
    const dt = parseVoDateToDate(raw);
    return dt ? formatLocalDateTimeSecond(dt) : undefined;
}

export function pickSpecialZoneHandoverFromVo(vo: SpecialZoneReservationOrderVoData): SpecialZoneHandover | undefined {
    return pickHandoverPayload(vo).hand;
}

export function pickSpecialZoneHandoverDetailsFromVo(vo: SpecialZoneReservationOrderVoData): SpecialZoneHandoverDetail[] {
    return pickHandoverPayload(vo).details;
}

export function pickHandoverConfirmSignatureIdFromVo(vo: SpecialZoneReservationOrderVoData): number | undefined {
    const hand = pickSpecialZoneHandoverFromVo(vo);
    if (!hand) return undefined;
    const rec = hand as unknown as Record<string, unknown>;
    const raw = hand.confirmSignatureId ?? rec.confirm_signature_id;
    if (raw == null || raw === "") return undefined;
    const id = Number(raw);
    if (!Number.isFinite(id) || id <= 0) return undefined;
    return id;
}

function pickHandoverTimeField(v: unknown): string {
    if (v == null) return "";
    if (typeof v === "string") return v.trim();
    const coerced = coerceVoDateToString(v);
    return coerced ? String(coerced).trim() : "";
}

export function formatHandoverConfirmTimeDisplay(hand: SpecialZoneHandover): string | undefined {
    const pick =
        pickHandoverTimeField(hand.loadingEndTime) ||
        pickHandoverTimeField(hand.confirmTime) ||
        pickHandoverTimeField(hand.updatedTime) ||
        pickHandoverTimeField(hand.createdTime);
    if (!pick) return undefined;
    return formatWeighLoadingEndTimeForDisplay(pick) ?? pick;
}

export function goodsNameFromHandoverDetail(d: SpecialZoneHandoverDetail): string {
    const gn = d.goodsName?.trim();
    if (gn) return gn;
    return goodsIntroByGoodsCode(d.goodsCode);
}

export function handoverHeaderFieldsFromReservationVo(vo: SpecialZoneReservationOrderVoData): {
    specialZoneHandoverId: string;
    confirmTimeDisplay: string;
} {
    const hand = pickSpecialZoneHandoverFromVo(vo);
    if (!hand) return { specialZoneHandoverId: "", confirmTimeDisplay: "" };
    const specialZoneHandoverId = hand.id != null ? String(hand.id).trim() : "";
    const confirmTimeDisplay = formatHandoverConfirmTimeDisplay(hand) ?? "";
    return { specialZoneHandoverId, confirmTimeDisplay };
}
